import { ChangeDetectionStrategy, Component, computed, input, type Signal } from '@angular/core';
import type { ConversationEvent } from '../../../../../../core/api/generated/models/ConversationEvent';
import { HistoryRowComponent } from './renderer-row';

const PRIORITY_LABEL: Record<string, string> = {
  low: 'baixa',
  normal: 'normal',
  high: 'alta',
  urgent: 'urgente',
};

const STATUS_LABEL: Record<string, string> = {
  open: 'aberta',
  pending: 'pendente',
  agent: 'agente',
  snoozed: 'pausada',
  resolved: 'resolvida',
  archived: 'arquivada',
  channel_unavailable: 'canal indisponível',
};

const END_REASON_LABEL: Record<string, string> = {
  handoff: 'handoff',
  resolved: 'resolvida',
  archived: 'arquivada',
  reopened: 'reaberta',
  reassigned: 'reatribuída',
  channel_unavailable: 'canal indisponível',
};

const ASSIGNEE_LABEL: Record<string, string> = {
  unassigned: 'sem responsável',
  membership: 'atendente',
  agent: 'agente IA',
};

const HANDOFF_LABEL: Record<string, string> = {
  agent_to_human: 'agente → humano',
  human_to_agent: 'humano → agente',
  human_to_human: 'humano → humano',
  agent_to_agent: 'agente → agente',
  agent_to_unassigned: 'agente → fila',
  human_to_unassigned: 'humano → fila',
};

function readableDuration(seconds: unknown): string | null {
  if (typeof seconds !== 'number' || Number.isNaN(seconds)) return null;
  if (seconds < 60) return `duração: ${seconds}s`;
  if (seconds < 3600) return `duração: ${Math.round(seconds / 60)} min`;
  return `duração: ${Math.round(seconds / 360) / 10}h`;
}

function readString(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Build the three shared signals every renderer needs from the event input. */
function rendererSignals(event: Signal<ConversationEvent>) {
  const payload = computed<Record<string, unknown>>(() => event().payload ?? {});
  return {
    payload,
    backfilled: computed(() => Boolean(payload()['backfilled'])),
    occurredAt: computed(() => event().occurredAt),
  };
}

/* === conversation lifecycle === */

@Component({
  selector: 'app-renderer-created',
  imports: [HistoryRowComponent],
  template: `
    <app-history-row
      icon="heroSparkles"
      title="Conversa criada"
      [detail]="detail()"
      [occurredAt]="ctx.occurredAt()"
      [backfilled]="ctx.backfilled()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationCreatedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly detail = computed(() => {
    const origin = readString(this.ctx.payload(), 'origin');
    return origin ? `Origem: ${origin === 'inbound' ? 'entrada' : 'saída'}` : null;
  });
}

@Component({
  selector: 'app-renderer-status-changed',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroArrowsRightLeft" [title]="title()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationStatusChangedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly title = computed(() => {
    const from = STATUS_LABEL[readString(this.ctx.payload(), 'fromStatus') ?? ''] ?? readString(this.ctx.payload(), 'fromStatus') ?? '?';
    const to = STATUS_LABEL[readString(this.ctx.payload(), 'toStatus') ?? ''] ?? readString(this.ctx.payload(), 'toStatus') ?? '?';
    return `Status: ${from} → ${to}`;
  });
}

@Component({
  selector: 'app-renderer-priority-changed',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroFlag" [title]="title()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationPriorityChangedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly title = computed(() => {
    const from = PRIORITY_LABEL[readString(this.ctx.payload(), 'fromPriority') ?? ''] ?? readString(this.ctx.payload(), 'fromPriority') ?? '?';
    const to = PRIORITY_LABEL[readString(this.ctx.payload(), 'toPriority') ?? ''] ?? readString(this.ctx.payload(), 'toPriority') ?? '?';
    return `Prioridade: ${from} → ${to}`;
  });
}

@Component({
  selector: 'app-renderer-snoozed',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroMoon" title="Conversa pausada" [detail]="detail()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationSnoozedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly detail = computed(() => {
    const until = readString(this.ctx.payload(), 'snoozedUntil');
    return until ? `até ${new Date(until).toLocaleString('pt-BR')}` : null;
  });
}

@Component({
  selector: 'app-renderer-resolved',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroCheckCircle" title="Conversa resolvida" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationResolvedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
}

@Component({
  selector: 'app-renderer-archived',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroArchiveBox" title="Conversa arquivada" [detail]="detail()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationArchivedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly detail = computed(() => readString(this.ctx.payload(), 'reason'));
}

@Component({
  selector: 'app-renderer-reopened',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroArrowUturnLeft" title="Conversa reaberta" [detail]="detail()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationReopenedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly detail = computed(() => {
    const reason = readString(this.ctx.payload(), 'reason');
    return reason && reason !== 'manual' ? `motivo: ${reason}` : null;
  });
}

@Component({
  selector: 'app-renderer-channel-unavailable',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroSignalSlash" title="Canal indisponível" [detail]="detail()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationChannelUnavailableRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly detail = computed(() => readString(this.ctx.payload(), 'reason'));
}

/* === labels === */

@Component({
  selector: 'app-renderer-label-added',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroTag" [title]="title()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationLabelAddedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly title = computed(() => `Label adicionada: ${readString(this.ctx.payload(), 'labelName') ?? '—'}`);
}

@Component({
  selector: 'app-renderer-label-removed',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroXMark" [title]="title()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationLabelRemovedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly title = computed(() => `Label removida: ${readString(this.ctx.payload(), 'labelName') ?? '—'}`);
}

/* === sessions === */

@Component({
  selector: 'app-renderer-session-opened',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroPlay" title="Sessão aberta" [detail]="detail()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionOpenedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly detail = computed(() => {
    const handoff = readString(this.ctx.payload(), 'handoffType');
    if (handoff) return HANDOFF_LABEL[handoff] ?? handoff;
    const assignee = readString(this.ctx.payload(), 'assigneeType');
    return assignee ? `responsável: ${ASSIGNEE_LABEL[assignee] ?? assignee}` : null;
  });
}

@Component({
  selector: 'app-renderer-session-closed',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroStop" title="Sessão encerrada" [detail]="detail()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionClosedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly detail = computed(() => {
    const reason = readString(this.ctx.payload(), 'endReason');
    const reasonLabel = reason ? END_REASON_LABEL[reason] ?? reason : null;
    const durationLabel = readableDuration(this.ctx.payload()['durationSeconds']);
    return [reasonLabel, durationLabel].filter(Boolean).join(' · ') || null;
  });
}

@Component({
  selector: 'app-renderer-session-assigned',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroUserPlus" title="Sessão atribuída" [detail]="detail()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionAssignedRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly detail = computed(() => {
    const handoff = readString(this.ctx.payload(), 'handoffType');
    return handoff ? HANDOFF_LABEL[handoff] ?? handoff : null;
  });
}

/* === fallback === */

@Component({
  selector: 'app-renderer-fallback',
  imports: [HistoryRowComponent],
  template: `<app-history-row icon="heroEllipsisHorizontal" [title]="title()" [occurredAt]="ctx.occurredAt()" [backfilled]="ctx.backfilled()" />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FallbackRendererComponent {
  readonly event = input.required<ConversationEvent>();
  protected readonly ctx = rendererSignals(this.event);
  protected readonly title = computed(() => `Evento: ${this.event().eventType}`);
}
